/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import API from "../lib/api";
import { Section, ArticleCard, CategoryCard, SkeletonCard } from "../components/Common";
import PractitionerRecommendations from "../components/PractitionerRecommendations";

// Wellness Hub content-category -> Marketplace topic mapping
const HUB_CATEGORY_TO_MARKETPLACE_TOPIC = {
  "mental-wellness": "mental-health",
  "mens-wellness": "mens-wellness",
  "womens-wellness": "womens-wellness",
  "nutrition": "nutrition",
  "sports-performance": "fitness",
  "recovery": "longevity",
  "menopause-andropause": "menopause",
  "postpartum": "womens-wellness",
  "parenting": "family-support",
  "relationships": "relationships",
  "sleep": "sleep",
  "stress-management": "burnout",
  "eating-disorder-awareness": "mental-health",
  "confidence": "mental-health",
};

export default function WellnessHubPage() {
  const { t } = useTranslation();
  const { category } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ categories: [], articles: [] });
  const [loading, setLoading] = useState(true);

  const CATEGORY_LANDING_OVERRIDES = {
    "mens-wellness": "/mens-wellness",
    "womens-wellness": "/womens-wellness",
    "relationships": "/relationships",
    "parenting": "/families",
  };

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (q) params.set("q", q);
    Promise.all([
      API.get("/content/categories"),
      API.get(`/content/articles?${params.toString()}`),
    ])
      .then(([cats, arts]) => setData({ categories: cats.data.categories || [], articles: arts.data.items || [] }))
      .finally(() => setLoading(false));
  }, [category, q]);

  return (
    <div data-testid="page-hub">
      <Section eyebrow="— Wellness Hub" title={t("hub.title")} subtitle={t("hub.subtitle")}>
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("hub.search_placeholder")}
              className="w-full bg-[#0F1116] border border-white/10 rounded-lg ps-9 pe-3 py-3 text-sm focus:outline-none focus:border-[#C8A24A]"
              data-testid="hub-search"
            />
          </div>
          <button
            onClick={() => navigate("/hub")}
            className={`chip ${!category ? "chip-active" : ""}`}
            data-testid="chip-all"
          >{t("hub.all")}</button>
        </div>
        <div className="mt-5 flex flex-wrap gap-2" data-testid="hub-categories">
          {data.categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => {
                const override = CATEGORY_LANDING_OVERRIDES[c.slug];
                if (override) navigate(override);
                else navigate(`/hub/category/${c.slug}`);
              }}
              className={`chip ${category === c.slug ? "chip-active" : ""}`}
              data-testid={`chip-${c.slug}`}
            >{c.title}</button>
          ))}
        </div>
      </Section>

      <div className="app-container pb-20">
        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} aspect="16/10" />)}
          </div>
        ) : data.articles.length === 0 ? (
          <div className="text-white/60 text-sm">{t("hub.empty")}</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="hub-articles">
            {data.articles.map((a) => <ArticleCard key={a.id} article={a} />)}
          </div>
        )}
      </div>

      {/* PRACTITIONERS — surfaced for the current category, or featured when no category */}
      <PractitionerRecommendations
        eyebrow={category ? "— Find a Pro" : "— The Marketplace"}
        title={
          category
            ? t("hub.practitioners_for", {
                defaultValue: "Practitioners for {{topic}}",
                topic: (data.categories.find((c) => c.slug === category)?.title) || category.replace(/-/g, " "),
              })
            : t("hub.practitioners_general", "Featured practitioners across the ecosystem")
        }
        subtitle={t("hub.practitioners_subtitle", "Hand-picked therapists, coaches, nutritionists, and studios from the DONiTSH!T Wellness Marketplace.")}
        topic={category ? HUB_CATEGORY_TO_MARKETPLACE_TOPIC[category] : undefined}
        limit={3}
        testid="hub-practitioner-recs"
      />
    </div>
  );
}
